export type TicketStatus =
  | "NEW"
  | "AUTOMATION_QUEUE"
  | "OPEN"
  | "WAITING_FOR_USER"
  | "RESOLVED"
  | "CLOSED"

export type TicketPriority = "LOW" | "NORMAL" | "HIGH"

export type MessageAuthorType = "USER" | "AGENT" | "SYSTEM"

export interface TicketShape {
  id: string
  user_id: string
  user_email?: string | null
  title: string
  description: string
  status: TicketStatus
  priority: TicketPriority
  category: string
  created_at: string
  updated_at: string
  messages_count?: number
  has_unread_activity?: boolean
}

export interface TicketMessageShape {
  id: string
  ticket_id: string
  author_type: MessageAuthorType
  author_id: string
  message: string
  created_at: string
}

export interface TicketListResponse {
  items: TicketShape[]
  total: number
  page: number
  page_size: number
}

export interface TicketMessageListResponse {
  items: TicketMessageShape[]
  total: number
  page: number
  page_size: number
}

export interface CreateTicketPayload {
  user_id: string
  title: string
  description?: string
}

export interface AddMessagePayload {
  author_type: MessageAuthorType
  author_id: string
  message: string
}

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  NEW: "New",
  AUTOMATION_QUEUE: "Automation Queue",
  OPEN: "Open",
  WAITING_FOR_USER: "Waiting for User",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
}

export type TicketCategory =
  | "REDEMPTION_REJECTED_INVALID_FARE"
  | "REDEMPTION_REJECTED_SHAREHOLDER_VERIFICATION"
  | "REDEMPTION_REJECTED_BOOKING_NOT_ELIGIBLE"
  | "REDEMPTION_REJECTED_TRAVEL_DATE"
  | "REDEMPTION_REJECTED_UNKNOWN"
  | "REDEMPTION_REJECTED_OTHER"
  | "REDEMPTION_NO_INSTRUCTIONS"
  | "REDEMPTION_UNUSABLE"
  | "REDEMPTION_OTHER"
  | "PROOF_EXPIRED"
  | "PROOF_APPROVED_NO_CLAIM"
  | "PROOF_REJECTED"
  | "PROOF_PENDING"
  | "PROOF_OTHER"
  | "BROKER_CREDENTIALS_ISSUE"
  | "BROKER_CONNECTION_OTHER"
  | "BROKER_OTHER"
  | "REQUEST_MY_DATA"
  | "REQUEST_ACCOUNT_DELETION"
  | "FEEDBACK"
  | "OTHER"
  | "EMAIL_VERIFICATION_ISSUE"
  | "PASSWORD_RESET_ISSUE"
  | "PUBLIC_OTHER"

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  REDEMPTION_REJECTED_INVALID_FARE: "Fare type not eligible",
  REDEMPTION_REJECTED_SHAREHOLDER_VERIFICATION:
    "Shareholder status not verified",
  REDEMPTION_REJECTED_BOOKING_NOT_ELIGIBLE: "Booking may not be eligible",
  REDEMPTION_REJECTED_TRAVEL_DATE: "Sailing date requirements",
  REDEMPTION_REJECTED_UNKNOWN: "Rejection reason unclear",
  REDEMPTION_REJECTED_OTHER: "Redemption rejected",
  REDEMPTION_NO_INSTRUCTIONS: "No redemption instructions received",
  REDEMPTION_UNUSABLE: "Redeemed perk cannot be used",
  REDEMPTION_OTHER: "Perk redemption issue",
  PROOF_EXPIRED: "Proof of ownership expired",
  PROOF_APPROVED_NO_CLAIM: "Proof approved but perks unavailable",
  PROOF_REJECTED: "Proof of ownership rejected",
  PROOF_PENDING: "Document still under review",
  PROOF_OTHER: "Proof of ownership issue",
  BROKER_CREDENTIALS_ISSUE: "Broker credentials issue",
  BROKER_CONNECTION_OTHER: "Broker connection issue",
  BROKER_OTHER: "Broker connection issue",
  REQUEST_MY_DATA: "Data request",
  REQUEST_ACCOUNT_DELETION: "Account deletion request",
  FEEDBACK: "Product feedback",
  OTHER: "General inquiry",
  EMAIL_VERIFICATION_ISSUE: "Email verification",
  PASSWORD_RESET_ISSUE: "Password reset",
  PUBLIC_OTHER: "General inquiry",
}

export const ALL_TICKET_STATUSES: TicketStatus[] = [
  "NEW",
  "AUTOMATION_QUEUE",
  "OPEN",
  "WAITING_FOR_USER",
  "RESOLVED",
  "CLOSED",
]

export const ALL_TICKET_CATEGORIES: TicketCategory[] = [
  "REDEMPTION_REJECTED_INVALID_FARE",
  "REDEMPTION_REJECTED_SHAREHOLDER_VERIFICATION",
  "REDEMPTION_REJECTED_BOOKING_NOT_ELIGIBLE",
  "REDEMPTION_REJECTED_TRAVEL_DATE",
  "REDEMPTION_REJECTED_UNKNOWN",
  "REDEMPTION_REJECTED_OTHER",
  "REDEMPTION_NO_INSTRUCTIONS",
  "REDEMPTION_UNUSABLE",
  "REDEMPTION_OTHER",
  "PROOF_EXPIRED",
  "PROOF_APPROVED_NO_CLAIM",
  "PROOF_REJECTED",
  "PROOF_PENDING",
  "PROOF_OTHER",
  "BROKER_CREDENTIALS_ISSUE",
  "BROKER_CONNECTION_OTHER",
  "BROKER_OTHER",
  "REQUEST_MY_DATA",
  "REQUEST_ACCOUNT_DELETION",
  "FEEDBACK",
  "OTHER",
  "EMAIL_VERIFICATION_ISSUE",
  "PASSWORD_RESET_ISSUE",
  "PUBLIC_OTHER",
]
